import { Component, createSignal, Show, createEffect, onCleanup } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { selfServiceApi, AccessCodeInfo, SessionInfo } from '@/services/selfservice';
import GroupLobby from './GroupLobby';
import LiveRanking from './LiveRanking';

type Step = 'code' | 'register' | 'ready' | 'lobby' | 'playing';

const SelfServicePage: Component = () => {
  const navigate = useNavigate();

  // State
  const [step, setStep] = createSignal<Step>('code');
  const [code, setCode] = createSignal('');
  const [teamName, setTeamName] = createSignal('');
  const [phoneNumber, setPhoneNumber] = createSignal('');
  const [codeInfo, setCodeInfo] = createSignal<AccessCodeInfo | null>(null);
  const [sessionInfo, setSessionInfo] = createSignal<SessionInfo | null>(null);
  const [teamPassword, setTeamPassword] = createSignal('');
  const [teamId, setTeamId] = createSignal<number | null>(null);
  const [error, setError] = createSignal('');
  const [loading, setLoading] = createSignal(false);
  const [timeRemaining, setTimeRemaining] = createSignal<number | null>(null);
  const [showRanking, setShowRanking] = createSignal(false);

  // Check if this is a group session
  const isGroupSession = () => {
    const session = sessionInfo();
    return session?.session_type === 'group';
  };

  // Check if current user is organizer
  const isOrganizer = () => {
    return codeInfo()?.is_organizer === true;
  };

  // Format code input (XXXX-XXXX)
  const formatCode = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
  };

  const handleCodeInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const formatted = formatCode(input.value);
    setCode(formatted);
  };

  // Timer for game countdown
  let timerInterval: number | undefined;

  createEffect(() => {
    const session = sessionInfo();
    if (session && session.status === 'active' && session.time_remaining_seconds) {
      setTimeRemaining(session.time_remaining_seconds);

      timerInterval = window.setInterval(() => {
        setTimeRemaining(prev => {
          if (prev === null || prev <= 0) {
            clearInterval(timerInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  });

  onCleanup(() => {
    if (timerInterval) clearInterval(timerInterval);
  });

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Step 1: Validate code
  const handleValidateCode = async () => {
    if (code().length < 9) {
      setError('Wprowadź pełny kod w formacie XXXX-XXXX');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await selfServiceApi.validateCode(code());
      if (response.valid) {
        setCodeInfo(response.code);

        // Check if already registered
        if (response.code.status === 'activated') {
          const statusResponse = await selfServiceApi.getCodeStatus(code());
          if (statusResponse.has_team && statusResponse.session) {
            setSessionInfo(statusResponse.session);

            // If team name exists in response, use it
            if (statusResponse.team?.name) {
              setTeamName(statusResponse.team.name);
            }

            if (statusResponse.session.status === 'active') {
              setStep('playing');
            } else if (statusResponse.session.session_type === 'group') {
              // Group session - go to lobby
              setStep('lobby');
            } else {
              setStep('ready');
            }
            return;
          }
        }

        setStep('register');
      }
    } catch (err: any) {
      const message = err.response?.data?.error || 'Błąd walidacji kodu';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Register team
  const handleRegister = async () => {
    if (teamName().trim().length < 2) {
      setError('Nazwa zespołu musi mieć minimum 2 znaki');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await selfServiceApi.registerTeam(code(), teamName().trim(), phoneNumber());
      setSessionInfo(response.session);
      setTeamPassword(response.team_password);
      setTeamId(response.team_id);

      // Check if group session - go to lobby instead of ready
      if (response.session.session_type === 'group') {
        setStep('lobby');
      } else {
        setStep('ready');
      }
    } catch (err: any) {
      const message = err.response?.data?.error || 'Błąd rejestracji zespołu';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Start game (solo only)
  const handleStartGame = async () => {
    const session = sessionInfo();
    if (!session) return;

    setLoading(true);
    setError('');

    try {
      const response = await selfServiceApi.startGame(session.id);
      setSessionInfo(response.session);
      setStep('playing');
    } catch (err: any) {
      const message = err.response?.data?.error || 'Błąd startu gry';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Callback from GroupLobby when game starts
  const handleGameStartFromLobby = (session: SessionInfo) => {
    setSessionInfo(session);
    setStep('playing');
  };

  // Navigate to main game
  const handleGoToGame = () => {
    // Store credentials for auto-login
    localStorage.setItem('selfservice_team_name', teamName() || codeInfo()?.template_name || '');
    localStorage.setItem('selfservice_team_password', teamPassword() || code().replace('-', ''));
    // Store access code and session ID for hints API
    localStorage.setItem('selfservice_access_code', code());
    localStorage.setItem('selfservice_session_id', String(sessionInfo()?.id || ''));
    localStorage.setItem('selfservice_template_id', String(sessionInfo()?.template_id || ''));
    navigate('/');
  };

  return (
    <div class="min-h-screen flex items-center justify-center p-4">
      <div class="w-full max-w-md">
        {/* Header */}
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-white mb-2">
            {codeInfo()?.template_name || 'Zakodowane Miasta'}
          </h1>
          <Show when={codeInfo()}>
            <p class="text-gray-300">{codeInfo()?.template_location}</p>
          </Show>
        </div>

        {/* Card */}
        <div class="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-xl border border-white/20">

          {/* Step 1: Enter Code */}
          <Show when={step() === 'code'}>
            <div class="space-y-6">
              <div class="text-center">
                <h2 class="text-xl font-semibold text-white mb-2">Wprowadź kod dostępu</h2>
                <p class="text-gray-300 text-sm">Znajdziesz go na karcie w pudełku z grą</p>
              </div>

              <div>
                <input
                  type="text"
                  value={code()}
                  onInput={handleCodeInput}
                  placeholder="XXXX-XXXX"
                  maxLength={9}
                  class="w-full px-4 py-4 text-2xl text-center font-mono tracking-widest
                         bg-white/10 border border-white/30 rounded-xl
                         text-white placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-all"
                  autofocus
                />
              </div>

              <Show when={error()}>
                <div class="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm text-center">
                  {error()}
                </div>
              </Show>

              <button
                onClick={handleValidateCode}
                disabled={loading() || code().length < 9}
                class="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-purple-600
                       text-white font-semibold rounded-xl
                       hover:from-blue-600 hover:to-purple-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all transform hover:scale-[1.02]
                       flex items-center justify-center gap-2"
              >
                <Show when={loading()} fallback="Dalej">
                  <span class="animate-spin">⏳</span> Sprawdzam...
                </Show>
              </button>
            </div>
          </Show>

          {/* Step 2: Register Team */}
          <Show when={step() === 'register'}>
            <div class="space-y-6">
              <div class="text-center">
                <h2 class="text-xl font-semibold text-white mb-2">Zarejestruj zespół</h2>
                <p class="text-gray-300 text-sm">Wybierz nazwę dla swojego zespołu</p>
              </div>

              <div class="bg-white/5 rounded-lg p-4 space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Gra:</span>
                  <span class="text-white">{codeInfo()?.template_name}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Czas gry:</span>
                  <span class="text-white">{codeInfo()?.max_duration_minutes} minut</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Ważność kodu:</span>
                  <span class="text-white">{codeInfo()?.validity_days} dni</span>
                </div>
                {/* Show role only for group sessions */}
                <Show when={codeInfo()?.package_type === 'group'}>
                  <div class="flex justify-between text-sm pt-2 border-t border-white/10">
                    <span class="text-gray-400">Typ:</span>
                    <span class="text-white flex items-center gap-2">
                      {codeInfo()?.is_organizer ? (
                        <><span class="text-yellow-400">👑</span> Organizator grupy</>
                      ) : (
                        <><span class="text-blue-400">👤</span> Uczestnik grupy</>
                      )}
                    </span>
                  </div>
                </Show>
              </div>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm text-gray-300 mb-2">Nazwa zespołu *</label>
                  <input
                    type="text"
                    value={teamName()}
                    onInput={(e) => setTeamName((e.target as HTMLInputElement).value)}
                    placeholder="np. Odkrywcy Miasta"
                    maxLength={100}
                    class="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl
                           text-white placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autofocus
                  />
                </div>

                <div>
                  <label class="block text-sm text-gray-300 mb-2">Telefon (opcjonalnie)</label>
                  <input
                    type="tel"
                    value={phoneNumber()}
                    onInput={(e) => setPhoneNumber((e.target as HTMLInputElement).value)}
                    placeholder="123 456 789"
                    class="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl
                           text-white placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p class="text-xs text-gray-400 mt-1">Na wypadek problemów technicznych</p>
                </div>
              </div>

              <Show when={error()}>
                <div class="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm text-center">
                  {error()}
                </div>
              </Show>

              <div class="flex gap-3">
                <button
                  onClick={() => setStep('code')}
                  class="flex-1 py-3 px-4 bg-white/10 text-white rounded-xl
                         hover:bg-white/20 transition-all"
                >
                  Wstecz
                </button>
                <button
                  onClick={handleRegister}
                  disabled={loading() || teamName().trim().length < 2}
                  class="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600
                         text-white font-semibold rounded-xl
                         hover:from-blue-600 hover:to-purple-700
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all"
                >
                  <Show when={loading()} fallback="Zarejestruj">
                    <span class="animate-spin">⏳</span>
                  </Show>
                </button>
              </div>
            </div>
          </Show>

          {/* Step 3: Ready to Start (Solo only) */}
          <Show when={step() === 'ready'}>
            <div class="space-y-6">
              <div class="text-center">
                <div class="text-6xl mb-4">🎮</div>
                <h2 class="text-xl font-semibold text-white mb-2">Gotowi do gry!</h2>
                <p class="text-gray-300 text-sm">
                  Zespół <span class="font-semibold text-white">{teamName() || 'Twój zespół'}</span> jest zarejestrowany
                </p>
              </div>

              <div class="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                <p class="text-green-200 text-sm mb-2">Czas gry:</p>
                <p class="text-3xl font-bold text-green-100">
                  {codeInfo()?.max_duration_minutes} minut
                </p>
              </div>

              <div class="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 text-yellow-200 text-sm">
                <p class="font-semibold mb-1">⚠️ Uwaga!</p>
                <p>Po kliknięciu "START" czas zacznie płynąć. Upewnij się, że jesteś gotowy!</p>
              </div>

              <Show when={error()}>
                <div class="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm text-center">
                  {error()}
                </div>
              </Show>

              <button
                onClick={handleStartGame}
                disabled={loading()}
                class="w-full py-5 px-6 bg-gradient-to-r from-green-500 to-emerald-600
                       text-white text-xl font-bold rounded-xl
                       hover:from-green-600 hover:to-emerald-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all transform hover:scale-[1.02]
                       shadow-lg shadow-green-500/30"
              >
                <Show when={loading()} fallback="🚀 START GRY">
                  <span class="animate-spin">⏳</span> Startuję...
                </Show>
              </button>
            </div>
          </Show>

          {/* Step 3b: Group Lobby */}
          <Show when={step() === 'lobby'}>
            <GroupLobby
              sessionId={sessionInfo()?.id || 0}
              currentTeamId={teamId()}
              isOrganizer={isOrganizer()}
              onGameStart={handleGameStartFromLobby}
              onBack={() => setStep('register')}
            />
          </Show>

          {/* Step 4: Game Started */}
          <Show when={step() === 'playing'}>
            <div class="space-y-6">
              <div class="text-center">
                <div class="text-6xl mb-4">🎯</div>
                <h2 class="text-xl font-semibold text-white mb-2">Gra rozpoczęta!</h2>
              </div>

              <Show when={timeRemaining() !== null}>
                <div class="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 text-center">
                  <p class="text-blue-200 text-sm mb-2">Pozostały czas:</p>
                  <p class="text-4xl font-mono font-bold text-blue-100">
                    {formatTime(timeRemaining()!)}
                  </p>
                </div>
              </Show>

              <div class="bg-white/5 rounded-lg p-4 space-y-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Zespół:</span>
                  <span class="text-white font-semibold">{teamName()}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Hasło:</span>
                  <span class="text-white font-mono">{teamPassword() || code().replace('-', '')}</span>
                </div>
                {/* Show group info if applicable */}
                <Show when={isGroupSession()}>
                  <div class="flex justify-between text-sm pt-2 border-t border-white/10">
                    <span class="text-gray-400">Tryb:</span>
                    <span class="text-white">👥 Gra grupowa</span>
                  </div>
                </Show>
              </div>

              {/* Live Ranking button for group sessions */}
              <Show when={isGroupSession()}>
                <button
                  onClick={() => setShowRanking(!showRanking())}
                  class="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600
                         text-white font-semibold rounded-xl
                         hover:from-amber-600 hover:to-orange-700
                         transition-all transform hover:scale-[1.02]
                         flex items-center justify-center gap-2"
                >
                  <span class="text-xl">🏆</span>
                  {showRanking() ? 'Ukryj ranking' : 'Pokaż Live Ranking'}
                </button>

                <Show when={showRanking()}>
                  <LiveRanking
                    sessionId={sessionInfo()?.id || 0}
                    accessCode={code()}
                    onClose={() => setShowRanking(false)}
                  />
                </Show>
              </Show>

              <button
                onClick={handleGoToGame}
                class="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-purple-600
                       text-white font-semibold rounded-xl
                       hover:from-blue-600 hover:to-purple-700
                       transition-all transform hover:scale-[1.02]"
              >
                Przejdź do gry →
              </button>

              <p class="text-center text-gray-400 text-xs">
                Możesz też zalogować się manualnie używając hasła zespołu
              </p>
            </div>
          </Show>
        </div>

        {/* Footer */}
        <div class="text-center mt-6 text-gray-400 text-sm">
          <p>Zakodowane Miasta • Self-Service</p>
        </div>
      </div>
    </div>
  );
};

export default SelfServicePage;
